<template>
  <div class="page-container your-profile">
    <div v-if="product" class="content-wrapper">
      <div class="left-section">
        <div class="inner-content">
          <h1>{{ product.your_profile_heading }}</h1>
          <p>{{ product.your_profile_description }}</p>

          <div class="profile-form-blocks">
            <bch-profile-blocks v-if="currentScheme === 'bch'" :profile="profile" :product="product" @add-driver="addAdditionalDriver" @driver-saved="onDriverSaved" @driver-deleted="onDriverDeleted" @employee-saved="onEmployeeSaved" @percentages-updated="onPercentagesUpdated"/>
            <salsac-profile-blocks v-if="currentScheme === 'salsac'" :profile="profile" :product="product" @add-driver="addAdditionalDriver" @driver-saved="onDriverSaved" @driver-deleted="onDriverDeleted" @employee-saved="onEmployeeSaved" @insurance-saved="onInsuranceSaved" @percentages-updated="onPercentagesUpdated"/>
          </div>

        </div>
      </div>
      <div class="right-section">
        <percentage-complete-block title="Profile Completion" description="Complete all sections to proceed" :percentage-completes="sectionPercentages" :loading="sidebarLoading">
          <div v-if="insuranceQuestionIssues && allSectionsComplete" class="insurance-issue-notice">
            Unfortunately due to the answer/s you have provided we are unable to proceed. Please contact the team using the Contact Us details at the bottom of the page.
            It may be possible that we can refer your details to the insurer. Please note there is no guarantee that the insurer will accept your circumstances,
            however if they can, an additional premium or surplus excess amount may be applicable.
          </div>
        </percentage-complete-block>
      </div>
    </div>
  </div>
</template>

<script>
import {EventBus} from "@/event-bus";
import {useRoute} from "vue-router";
import CardWithSlidedown from "@/components/ui/card-with-slidedown.vue";
import PercentageComplete from "@/components/ui/percentage-complete.vue";
import PercentageCompleteBlock from "@/components/ui/percentage-complete-block.vue";
import BchProfileBlocks from "@/components/your-profile/bch/profile-blocks.vue";
import SalsacProfileBlocks from "@/components/your-profile/salsac/profile-blocks.vue";
import {zeroIfNan} from "@/helpers/utils";

export default {
  name: "your-profile-component",
  components: {PercentageCompleteBlock, PercentageComplete, CardWithSlidedown, BchProfileBlocks, SalsacProfileBlocks},

  props: [
    'session',
    'adminPortalData',
    'adminPortalPrimaryProduct'
  ],

  data: () => {
    return {
      profile: null,
      driverGrades: null,
      sectionPercentages: [],
      sidebarLoading: true,
      profileCompleted: false,
      insuranceQuestionIssues: false,
    }
  },

  setup() {
    const route = useRoute();
    return {route}
  },

  mounted() {
    this.get();
  },

  computed: {
    product() {
      return this.adminPortalData?.content?.products?.[this.currentScheme][0];
    },
    currentScheme() {
      return this.route.params.scheme;
    },
    allSectionsComplete() {
      return this.sectionPercentages.every(s => s.percent >= 100);
    },
    canContinue() {
      if (!this.profile) return false;
      return this.allSectionsComplete && this.profileCompleted && !this.insuranceQuestionIssues;
    },
  },

  watch: {
    canContinue(val) {
      EventBus.$emit('yourProfileCompleted', val);
    },
  },

  methods: {
    get(customLoadingTitle) {
      const loadingTitle = customLoadingTitle ?? 'Fetching Driver Profile';

      EventBus.$emit('loadingTitle', loadingTitle);
      EventBus.$emit('loading', true);

      this.$http.get('/api/your-profile/' + this.currentScheme)
        .then(response => {
          this.$root.session = response.data.session;
          const details = response.data.data;
          this.prepareDriverProfileData(details);

          if (response.data.redirect) {
            this.$router.push({name: response.data.redirect, params: {...this.$route.params}});
          }
        })
        .catch(err => {
          let response = err.response;
          console.log(err);

          this.$notify({
            group: 'notify',
            type: 'error',
            title: 'Error',
            text: response.message
          });

          this.error = err.message.toString();
          this.$router.push({name: response.redirect, params: {...this.$route.params}});
        }).finally(() => {
        EventBus.$emit('loading', false);
      });
    },

    createAdditionalDriverObject(data = null) {
      const defaults = {
        id: null,
        firstname: '',
        surname: '',
        postcode: '',
        email: '',
        dob: '',
        mainDriver: false,
        drivingLicense: '',
        insuranceQuestions: {},
      };

      if (!data) return defaults;

      return {
        ...defaults,
        id: data.id,
        fullName: data.first_name + ' ' + data.last_name,
        firstname: data.first_name,
        surname: data.last_name,
        postcode: data.postcode,
        email: data.email,
        dob: data.date_of_birth,
        mainDriver: data.main_driver ? true : false,
        drivingLicense: data.driving_license,
        insuranceQuestions: data.insurance_questions,
        additionalDriverID: data.id
      };
    },

    addAdditionalDriver() {
      const limit = zeroIfNan(this.product?.maximum_additional_drivers);
      if ((this.profile?.additionalDrivers?.length ?? 0) >= limit) return;
      this.profile.additionalDrivers.push(this.createAdditionalDriverObject());
    },

    onDriverSaved({ index, apiData }) {
      const updatedDriver = this.createAdditionalDriverObject(apiData);
      this.profile.additionalDrivers.splice(index, 1, updatedDriver);

      // Only one driver can be the main driver — if this one is, clear the flag on all others
      if (updatedDriver.mainDriver) {
        this.profile.mainDriver = false;
        this.profile.additionalDrivers.forEach((driver, i) => {
          if (i !== index) driver.mainDriver = false;
        });
      }

      if (apiData?.profile?.profileCompleted !== undefined) {
        this.profileCompleted = apiData.profile.profileCompleted;
      }
      if (apiData?.profile?.insuranceQuestionIssues !== undefined) {
        this.insuranceQuestionIssues = apiData.profile.insuranceQuestionIssues;
      }
    },

    onDriverDeleted({ index, profileData }) {
      this.profile.additionalDrivers.splice(index, 1);
      if (profileData?.profileCompleted !== undefined) {
        this.profileCompleted = profileData.profileCompleted;
      }
      if (profileData?.insuranceQuestionIssues !== undefined) {
        this.insuranceQuestionIssues = profileData.insuranceQuestionIssues;
      }
    },

    onEmployeeSaved() {
      this.get('Updating Driver Profile');
    },

    onInsuranceSaved(data) {
      if (this.profile) {
        this.profile.primaryOvernightVehicleLocation = data.primaryOvernightVehicleLocation;
        this.profile.acceptanceOfSalarySacrificeConditionsOfUseAndInsurance = data.acceptanceOfSalarySacrificeConditionsOfUseAndInsurance;
      }
      if (data.profileCompleted !== undefined) this.profileCompleted = data.profileCompleted;
      if (data.insuranceQuestionIssues !== undefined) this.insuranceQuestionIssues = data.insuranceQuestionIssues;
    },

    onPercentagesUpdated(percentages) {
      this.sectionPercentages = percentages;
      if (this.profile) {
        this.sidebarLoading = false;
      }
    },

    prepareDriverProfileData(details) {
      const profile = {};
      profile.driverID = details.driverID;
      profile.fullName = details.contacts[0].fullName;
      profile.forename = details.firstName;
      profile.surname = details.surname;
      profile.email = details.contacts[0].emailAddress;
      profile.mobile = details.contacts[0].mobile;
      profile.homeAddress = details.homeAddress ? details.homeAddress.replace(/(?:\r\n|\r|\n)/g, ', ') : '';
      profile.dob = details.dateOfBirth;

      const n = profile.homeAddress.lastIndexOf(',');
      profile.postcode = profile.homeAddress.substring(n + 1)
      profile.homeAddress = profile.homeAddress.substring(0, n);

      profile.incomeTaxBand = details.incomeTaxBand.ratePercent;
      profile.companyCarGrade = details.driverGrades[0].name;
      profile.privateCarGrade = details.driverGrades.map(grade => grade.name).join(", ");
      profile.privateCarCashAllowance = details.currentCashAllowances.totalMonthlyCurrentAllowance;
      profile.totalAnnualMileage = Number(details.annualBusinessDistance) + Number(details.annualPrivateDistance);
      profile.businessAnnualMileage = Number(details.annualBusinessDistance);
      profile.privateAnnualMileage = Number(details.annualPrivateDistance);
      profile.driverLicence = details.driverLicence.licenceNumber;
      profile.insuranceQuestions = details.insuranceQuestions;
      profile.additionalDrivers = details.additionalDrivers;
      profile.mainDriver = details.mainDriver ? true : false;

      if (profile.additionalDrivers) {
        profile.additionalDrivers = profile.additionalDrivers.map(
          driver => this.createAdditionalDriverObject(driver)
        );
      } else {
        profile.additionalDrivers = [];
      }

      profile.primaryOvernightVehicleLocation = details.primaryOvernightVehicleLocation;
      profile.acceptanceOfSalarySacrificeConditionsOfUseAndInsurance = details.acceptanceOfSalarySacrificeConditionsOfUseAndInsurance;

      this.profileCompleted = details.profileCompleted ?? false;
      this.insuranceQuestionIssues = details.insuranceQuestionIssues ?? false;
      this.profile = profile;
    }
  }
}
</script>

<style scoped lang="scss">
.insurance-issue-notice {
  margin-top: 20px;
  padding: 14px 16px;
  background: rgba(220, 38, 38, 0.05);
  border: 1px solid rgba(220, 38, 38, 0.4);
  border-radius: 5px;
  font-size: 13px;
  line-height: 1.6;
  color: #333;
}

h1 {
  font-size: 32px;
  margin-bottom: 10px;
}
.page-container {
  width: 100%;
  align-items: flex-start;
  padding-bottom: 100px;
}

.content-wrapper {
  padding: 0 12px;
  display: flex;
  gap: 0;
  justify-content: center;
  width: 100%;
  max-width: 1470px;
  margin: 0 auto;
}

.left-section {
  flex-basis: auto;
  max-width: 970px;
  flex-shrink: 1;
  flex-grow: 1;
  min-width: 0;
}

.right-section {
  flex-basis: 500px;
  max-width: 500px;
  flex-shrink: 0;
  flex-grow: 1;
  background: #fff;
  box-shadow: 0 0 0 100vw #fff;
  clip-path: inset(0 -100vw 0 0);
}

.inner-content {
  padding: 35px 50px;
}

.profile-form-blocks {
  display: flex;
  flex-direction: column;
  gap: 20px;
}
</style>
